/*
 * YAGA collection finder: compact instant product search + YAGA sidebar filters
 * ("On sale" under Price on every collection; Grow type + Highlights on flower collections).
 *
 * The collection grid is paginated, so filtering only the visible cards would miss most products.
 * When a search or a YAGA filter is active, every page of the current collection view (native
 * filters + sort kept) is loaded once through the Section Rendering API and the real product cards
 * are filtered and shown in place. Clearing everything restores the original page and pagination.
 * State is kept in the URL (?q=…&sale=yes&grow=indoor&highlight=craft-batch).
 */
(() => {
  const root = document.querySelector('[data-yaga-finder]');
  if (!root) return;

  const tools = root.closest('.yaga-cro-collection-tools');
  const input = root.querySelector('[data-yaga-finder-input]');
  const clearButton = root.querySelector('[data-yaga-finder-clear]');
  const toggleButton = root.querySelector('[data-yaga-finder-toggle]');
  const message = root.querySelector('[data-yaga-finder-message]');
  const pillTemplate = root.querySelector('[data-yaga-pill-template]');
  const sectionId = root.dataset.sectionId;

  // YAGA filter groups: URL param, pill prefix, and how multiple values combine.
  const GROUPS = {
    sale: { param: 'sale', prefix: 'Price', any: true },
    grow: { param: 'grow', prefix: 'Grow type', any: true },
    highlight: { param: 'highlight', prefix: 'Highlights', any: false },
  };
  const GROUP_KEYS = Object.keys(GROUPS);
  const OWN_PARAMS = ['q', ...GROUP_KEYS.map((key) => GROUPS[key].param)];
  const VIEW_IGNORED_PARAMS = [...OWN_PARAMS, 'page', 'section_id'];
  const MAX_PAGES = 20;
  const compactQuery = window.matchMedia('(max-width: 989px)');

  const state = { q: '', filters: Object.fromEntries(GROUP_KEYS.map((key) => [key, new Set()])) };
  const cache = new Map();
  const ready = new Set();
  let native = null;
  let renderToken = 0;
  let pageOnly = false;
  let lastTrackedQuery = '';
  let trackTimer = null;

  const track = (event, details = {}) => {
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event, ...details });
    document.dispatchEvent(new CustomEvent(`yaga:${event}`, { detail: details }));
  };

  const normalize = (value) =>
    (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9.%]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const getContainer = () => document.getElementById('ProductGridContainer');
  const getGrid = () => document.getElementById('product-grid');
  // Desktop sort row, mobile "Filter and sort" row and the drawer header.
  const getCountElements = () =>
    [
      document.getElementById('ProductCountDesktop'),
      document.getElementById('ProductCount'),
      ...document.querySelectorAll('.mobile-facets__count'),
    ].filter(Boolean);
  const hasFilters = () => GROUP_KEYS.some((key) => state.filters[key].size > 0);
  const isActive = () => Boolean(state.q.trim()) || hasFilters();

  /* ---------- Native (server-rendered) page snapshot ---------- */

  const captureNative = () => {
    const grid = getGrid();
    native = grid
      ? {
          grid,
          children: [...grid.children],
          pagination: getContainer()?.querySelector('.pagination-wrapper') || null,
          counts: getCountElements().map((element) => [element, element.innerHTML]),
        }
      : null;
  };

  const restoreNative = () => {
    const grid = getGrid();
    if (grid) delete grid.dataset.yagaFinderPending;
    if (!native || grid !== native.grid || grid.dataset.yagaFinderActive !== 'true') return;
    grid.replaceChildren(...native.children);
    delete grid.dataset.yagaFinderActive;
    if (native.pagination) native.pagination.hidden = false;
    native.counts.forEach(([element, html]) => {
      if (element.isConnected) element.innerHTML = html;
    });
  };

  /* ---------- Full collection index (all pages of the current view) ---------- */

  // Native filters and sort from the URL; our own params and the page number don't change the set.
  const viewKey = () => {
    const params = new URLSearchParams(window.location.search);
    VIEW_IGNORED_PARAMS.forEach((name) => params.delete(name));
    return params.toString();
  };

  const toItem = (card, index) => ({
    card,
    index,
    values: {
      sale: card.dataset.yagaSale === 'true' ? ['yes'] : [],
      grow: card.dataset.yagaGrow ? [card.dataset.yagaGrow] : [],
      highlight: (card.dataset.yagaHighlight || '').split(/\s+/).filter(Boolean),
    },
    title: normalize(card.dataset.yagaTitle),
    haystack: normalize(card.dataset.yagaSearch || card.textContent),
  });

  const fetchPage = async (key, page) => {
    const params = new URLSearchParams(key);
    params.set('section_id', sectionId);
    params.set('page', String(page));
    const response = await fetch(`${window.location.pathname}?${params}`);
    if (!response.ok) throw new Error(`Section request failed (${response.status})`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  const loadIndex = (key) => {
    if (cache.has(key)) return cache.get(key);
    const promise = (async () => {
      const first = await fetchPage(key, 1);
      const pages = Math.min(MAX_PAGES, Math.max(1, Number(first.getElementById('product-grid')?.dataset.yagaPages) || 1));
      const rest = await Promise.all(Array.from({ length: pages - 1 }, (_, index) => fetchPage(key, index + 2)));
      const seen = new Set();
      const items = [];
      [first, ...rest].forEach((doc) => {
        doc.querySelectorAll('#product-grid > [data-yaga-card]').forEach((card) => {
          const id = card.dataset.yagaProductId;
          if (id && seen.has(id)) return;
          if (id) seen.add(id);
          items.push(toItem(card, items.length));
        });
      });
      return items;
    })();
    cache.set(key, promise);
    promise.then(
      () => {
        ready.add(key);
        updateFilterCounts();
      },
      () => cache.delete(key)
    );
    return promise;
  };

  const pageItems = () =>
    (native?.children || []).filter((node) => node.matches?.('[data-yaga-card]')).map((card, index) => toItem(card, index));

  /* ---------- Matching ---------- */

  const tokensOf = (query) => normalize(query).split(' ').filter(Boolean);

  // 1-2 letters match the start of a word ("in" → Indica, Indoor); longer terms match anywhere.
  const tokenMatches = (text, token) => (token.length > 2 ? text.includes(token) : ` ${text}`.includes(` ${token}`));
  const matchesQuery = (item, tokens) => tokens.every((token) => tokenMatches(item.haystack, token));

  const passesGroup = (item, key, selected = state.filters[key]) => {
    if (!selected.size) return true;
    const values = item.values[key];
    return GROUPS[key].any ? values.some((value) => selected.has(value)) : [...selected].every((value) => values.includes(value));
  };
  const passesGroups = (item, except = null) => GROUP_KEYS.every((key) => key === except || passesGroup(item, key));

  const rank = (item, tokens, phrase) => {
    if (!tokens.length) return 0;
    if (item.title.startsWith(phrase)) return 0;
    if (tokens.every((token) => ` ${item.title}`.includes(` ${token}`))) return 1;
    if (tokens.every((token) => item.title.includes(token))) return 2;
    return 3;
  };

  // Title matches go first, unless the shopper picked a sort (e.g. price) – then keep that order.
  const useRelevance = () => {
    const sort = new URLSearchParams(window.location.search).get('sort_by');
    return !sort || sort === root.dataset.defaultSort;
  };

  const filterItems = (items) => {
    const tokens = tokensOf(state.q);
    const phrase = tokens.join(' ');
    const relevance = useRelevance();
    return items
      .filter((item) => passesGroups(item) && matchesQuery(item, tokens))
      .map((item) => ({ item, score: relevance ? rank(item, tokens, phrase) : 0 }))
      .sort((a, b) => a.score - b.score || a.item.index - b.item.index)
      .map(({ item }) => item);
  };

  /* ---------- Sidebar filters: checkboxes, counts, selected badges, active-filter pills ---------- */

  const filterInputs = () => [...document.querySelectorAll('[data-yaga-custom-filter]')];

  const optionLabel = (key, value) => {
    const input = document.querySelector(`[data-yaga-custom-filter="${key}"][value="${CSS.escape(value)}"]`);
    return input?.closest('label')?.querySelector('.facet-checkbox__text-label')?.textContent.trim() || value;
  };

  // Count per option = products matching the search and the other groups, with this option.
  function updateFilterCounts() {
    const key = viewKey();
    const counters = document.querySelectorAll('[data-yaga-filter-count]');
    if (!counters.length) return;
    if (!ready.has(key)) {
      counters.forEach((element) => {
        if (element.textContent) element.textContent = '';
      });
      return;
    }
    cache.get(key).then((items) => {
      if (key !== viewKey()) return;
      const tokens = tokensOf(state.q);
      const base = items.filter((item) => matchesQuery(item, tokens));
      document.querySelectorAll('[data-yaga-filter-count]').forEach((element) => {
        const [group, value] = element.dataset.yagaFilterCount.split(':');
        const total = base.filter((item) => passesGroups(item, group) && item.values[group]?.includes(value)).length;
        const text = `(${total})`;
        if (element.textContent !== text) element.textContent = text;
        const label = element.closest('label');
        const inputEl = label?.querySelector('input');
        const disabled = total === 0 && !inputEl?.checked;
        label?.classList.toggle('disabled', disabled);
        if (inputEl && inputEl.disabled !== disabled) inputEl.disabled = disabled;
      });
    });
  }

  const activePills = () =>
    GROUP_KEYS.flatMap((key) =>
      [...state.filters[key]].map((value) => ({
        id: `${key}:${value}`,
        text: `${GROUPS[key].prefix}: ${key === 'sale' ? 'On sale' : optionLabel(key, value)}`,
      }))
    );

  const syncFilterUI = () => {
    filterInputs().forEach((inputEl) => {
      const on = state.filters[inputEl.dataset.yagaCustomFilter]?.has(inputEl.value) || false;
      if (inputEl.checked !== on) inputEl.checked = on;
      inputEl.closest('label')?.classList.toggle('active', on);
    });

    document.querySelectorAll('[data-yaga-facet-selected]').forEach((badge) => {
      const size = state.filters[badge.dataset.yagaFacetSelected]?.size || 0;
      const text = size ? `(${size})` : '';
      if (badge.textContent !== text) badge.textContent = text;
      badge.classList.toggle('hidden', !size);
    });

    const pills = activePills();
    [
      [document.querySelector('.active-facets-desktop'), (container, pill) => container.append(pill)],
      [
        document.querySelector('.active-facets-mobile'),
        (container, pill) => container.insertBefore(pill, container.querySelector(':scope > .active-facets__button-wrapper')),
      ],
    ].forEach(([container, insert]) => {
      if (!container || !pillTemplate) return;
      const wanted = new Set(pills.map((pill) => pill.id));
      container.querySelectorAll('[data-yaga-pill]').forEach((pill) => {
        if (!wanted.has(pill.dataset.yagaPill)) pill.remove();
      });
      pills.forEach(({ id, text }) => {
        let pill = container.querySelector(`[data-yaga-pill="${CSS.escape(id)}"]`);
        if (!pill) {
          pill = pillTemplate.content.firstElementChild.cloneNode(true);
          pill.dataset.yagaPill = id;
          insert(container, pill);
        }
        const label = pill.querySelector('[data-yaga-pill-label]');
        if (label && label.textContent !== text) label.textContent = text;
      });
    });

    updateFilterCounts();
  };

  /* ---------- Rendering ---------- */

  const filterSummary = () =>
    activePills()
      .map((pill) => pill.text.split(': ').pop())
      .join(', ');

  const describe = (count) => {
    const query = state.q.trim();
    const noun = count === 1 ? 'product' : 'products';
    const summary = filterSummary();
    const scope = pageOnly ? ' (this page only)' : '';
    if (!count) {
      if (query) return `No products match “${query}”${summary ? ` with ${summary}` : ''}${scope}.`;
      return `No products match ${summary || 'these filters'}${scope}.`;
    }
    const verb = count === 1 ? 'matches' : 'match';
    return `${count} ${noun}${query ? ` ${verb} “${query}”` : ''}${summary ? ` · ${summary}` : ''}${scope}`;
  };

  const setMessage = (text) => {
    if (message.textContent !== text) message.textContent = text;
  };

  const emptyState = () => {
    const item = document.createElement('li');
    item.className = 'yaga-finder-empty';
    const text = document.createElement('p');
    text.textContent = describe(0);
    const actions = document.createElement('div');
    actions.className = 'yaga-finder-empty__actions';
    const reset = document.createElement('button');
    reset.type = 'button';
    const query = state.q.trim();
    reset.textContent = query && hasFilters() ? 'Clear search and filters' : query ? 'Clear search' : 'Clear filters';
    reset.addEventListener('click', () => resetAll());
    actions.append(reset);
    if (query) {
      const link = document.createElement('a');
      link.href = `${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
      link.textContent = 'Search the whole store';
      actions.append(link);
    }
    item.append(text, actions);
    return item;
  };

  const prepareCard = (card) => {
    const node = document.importNode(card, true);
    node.hidden = false;
    node.removeAttribute('aria-hidden');
    [node, ...node.querySelectorAll('.scroll-trigger')].forEach((element) => {
      if (element.classList.contains('scroll-trigger')) element.classList.add('scroll-trigger--cancel');
    });
    return node;
  };

  const showResults = (items) => {
    const grid = getGrid();
    if (!native || grid !== native.grid) return;
    const nodes = items.map((item) => prepareCard(item.card));
    grid.replaceChildren(...(nodes.length ? nodes : [emptyState()]));
    grid.dataset.yagaFinderActive = 'true';
    delete grid.dataset.yagaFinderPending;
    if (native.pagination) native.pagination.hidden = true;
    const count = `${items.length} ${items.length === 1 ? 'product' : 'products'}`;
    getCountElements().forEach((element) => {
      if (element.textContent.trim() !== count) element.textContent = count;
    });
    setMessage(describe(items.length));
    updateFilterCounts();
    scheduleSearchTracking(items.length);
  };

  const setLoading = (loading) => {
    root.classList.toggle('is-loading', loading);
    getContainer()?.querySelector('.collection')?.classList.toggle('loading', loading);
  };

  const scrollToResults = () => {
    const container = getContainer();
    if (!container || !tools) return;
    const offset = tools.getBoundingClientRect().bottom + 12;
    const top = container.getBoundingClientRect().top;
    // Only when the results start above the sticky bar (the shopper had scrolled down the grid).
    if (top < offset - 24) window.scrollTo({ top: window.scrollY + top - offset, behavior: 'smooth' });
  };

  const isOpen = () => root.classList.contains('is-open');

  const setOpen = (open) => {
    root.classList.toggle('is-open', open);
    tools?.classList.toggle('is-searching', open && compactQuery.matches);
    toggleButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    syncControls();
  };

  function syncControls() {
    if (input.value !== state.q) input.value = state.q;
    // Collapsed layouts need the × to close the field even when it is empty.
    clearButton.hidden = !state.q && !(compactQuery.matches && isOpen());
    root.classList.toggle('is-active', isActive());
  }

  const syncUrl = () => {
    const url = new URL(window.location.href);
    const query = state.q.trim();
    if (query) url.searchParams.set('q', query);
    else url.searchParams.delete('q');
    GROUP_KEYS.forEach((key) => {
      const values = [...state.filters[key]];
      if (values.length) url.searchParams.set(GROUPS[key].param, values.join(','));
      else url.searchParams.delete(GROUPS[key].param);
    });
    if (url.href !== window.location.href) history.replaceState(history.state, '', url.href);
  };

  const render = ({ scroll = false } = {}) => {
    const token = ++renderToken;
    syncControls();
    syncFilterUI();
    syncUrl();

    if (!isActive()) {
      setLoading(false);
      restoreNative();
      setMessage('');
      return;
    }

    if (!native || native.grid !== getGrid()) captureNative();
    if (!native) return;

    // Unfiltered cards are on screen (first use, or native filters/sort just re-rendered the grid):
    // keep them hidden behind the loading overlay until the filtered results are ready.
    const key = viewKey();
    if (native.grid.dataset.yagaFinderActive !== 'true' && !ready.has(key)) {
      native.grid.dataset.yagaFinderPending = 'true';
      setLoading(true);
    }

    let settled = false;
    window.setTimeout(() => {
      if (!settled && token === renderToken) setLoading(true);
    }, 80);

    loadIndex(key)
      .then((items) => {
        if (token !== renderToken) return;
        pageOnly = false;
        showResults(filterItems(items));
        if (scroll) scrollToResults();
      })
      .catch((error) => {
        if (token !== renderToken) return;
        console.warn('[yaga-finder] Could not load all pages, filtering this page only.', error);
        pageOnly = true;
        showResults(filterItems(pageItems()));
      })
      .finally(() => {
        settled = true;
        if (token === renderToken) setLoading(false);
      });
  };

  const clearFilters = (keys = GROUP_KEYS) => keys.forEach((key) => state.filters[key].clear());

  const resetAll = () => {
    state.q = '';
    clearFilters();
    if (compactQuery.matches) setOpen(false);
    render();
  };

  const setFilterValue = (key, value, on) => {
    const set = state.filters[key];
    if (!set || set.has(value) === on) return;
    if (on) set.add(value);
    else set.delete(value);
    render({ scroll: true });
    track('collection_filter', { filter: key, value, on });
    if (key === 'sale') track('collection_sale_filter', { value: on ? 'yes' : 'all' });
  };

  const scheduleSearchTracking = (results) => {
    window.clearTimeout(trackTimer);
    const query = state.q.trim();
    if (query.length < 2) return;
    trackTimer = window.setTimeout(() => {
      if (query !== state.q.trim() || query === lastTrackedQuery) return;
      lastTrackedQuery = query;
      track('collection_search', { query, results });
    }, 1200);
  };

  const readStateFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    state.q = params.get('q') || '';
    GROUP_KEYS.forEach((key) => {
      const values = (params.get(GROUPS[key].param) || '').split(',').map((value) => value.trim()).filter(Boolean);
      state.filters[key] = new Set(key === 'sale' ? values.filter((value) => value === 'yes') : values);
    });
  };

  /* ---------- Search events ---------- */

  const warm = () => {
    loadIndex(viewKey()).catch(() => {});
  };

  let inputTimer = null;
  input.addEventListener('input', () => {
    state.q = input.value;
    syncControls();
    window.clearTimeout(inputTimer);
    if (!state.q.trim()) {
      render();
      return;
    }
    inputTimer = window.setTimeout(() => render(), 140);
  });

  const clearSearch = () => {
    window.clearTimeout(inputTimer);
    state.q = '';
    render();
    if (compactQuery.matches) {
      setOpen(false);
      toggleButton.focus();
    } else {
      input.focus();
    }
  };

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && (input.value || (compactQuery.matches && isOpen()))) {
      event.preventDefault();
      clearSearch();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      window.clearTimeout(inputTimer);
      render({ scroll: true });
      if (window.matchMedia('(pointer: coarse)').matches) input.blur();
    }
  });

  clearButton.addEventListener('click', clearSearch);

  toggleButton.addEventListener('click', () => {
    setOpen(true);
    input.focus();
    warm();
  });

  // Collapsed layouts: an empty field closes again when focus leaves it.
  root.addEventListener('focusout', (event) => {
    if (!compactQuery.matches || state.q.trim() || root.contains(event.relatedTarget)) return;
    window.setTimeout(() => {
      if (!root.contains(document.activeElement) && !state.q.trim()) setOpen(false);
    }, 0);
  });

  input.addEventListener('focus', warm);
  root.addEventListener('pointerenter', warm);
  root.addEventListener('touchstart', warm, { passive: true });

  /* ---------- Sidebar filter events ---------- */

  // The checkboxes live inside the theme's filter form. Keep facets.js from treating them as native
  // filters (they have no name, but the form would still re-render): stop them in the capture phase.
  const onFilterInput = (event) => {
    const inputEl = event.target.closest?.('[data-yaga-custom-filter]');
    if (!inputEl) return;
    event.stopPropagation();
    if (event.type === 'change') setFilterValue(inputEl.dataset.yagaCustomFilter, inputEl.value, inputEl.checked);
  };
  document.addEventListener('input', onFilterInput, true);
  document.addEventListener('change', onFilterInput, true);

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!target?.closest) return;

      const pill = target.closest('[data-yaga-pill]');
      if (pill) {
        event.preventDefault();
        const [key, value] = pill.dataset.yagaPill.split(':');
        setFilterValue(key, value, false);
        return;
      }

      const groupClear = target.closest('[data-yaga-custom-clear]');
      if (groupClear) {
        event.preventDefault();
        clearFilters([groupClear.dataset.yagaCustomClear]);
        render();
        return;
      }

      // Theme "Remove all" / mobile "Clear all" clear native filters: clear the search and ours too.
      // The mobile Price "Clear" clears the price range: clear "On sale" with it.
      const clearAll = target.closest('.active-facets__button-remove, .mobile-facets__clear');
      if (!clearAll) return;
      // (The whole mobile drawer is a <details> too, so match the Price submenu itself.)
      if (clearAll.closest('.mobile-facets__details')?.querySelector('[data-yaga-custom-filter="sale"]')) {
        clearFilters(['sale']);
      } else {
        state.q = '';
        clearFilters();
        if (compactQuery.matches) setOpen(false);
      }
      syncControls();
      syncFilterUI();
      syncUrl();
      setMessage('');
    },
    true
  );

  // Warm the index when a filter with YAGA options opens, so the counts appear.
  document.addEventListener(
    'toggle',
    (event) => {
      if (event.target.open && event.target.querySelector?.('[data-yaga-custom-filter]')) warm();
    },
    true
  );

  /* ---------- Theme re-renders ---------- */

  // Native filters / sort re-render the grid (facets.js replaces the container's content).
  const container = getContainer();
  if (container) {
    new MutationObserver(() => {
      if (native && getGrid() === native.grid) return;
      captureNative();
      if (isActive()) render();
      else updateFilterCounts();
    }).observe(container, { childList: true });
  }

  // facets.js also re-renders the filter panels and active-filter pills from server HTML, which
  // doesn't know about the YAGA filters: put their state and pills back after every update.
  const filters = document.getElementById('main-collection-filters');
  if (filters) {
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        syncFilterUI();
      });
    }).observe(filters, { childList: true, subtree: true });
  }

  window.addEventListener('popstate', () => {
    readStateFromUrl();
    setOpen(Boolean(state.q.trim()));
    render();
  });

  compactQuery.addEventListener?.('change', () => setOpen(isOpen() || Boolean(state.q.trim())));

  /* ---------- Init ---------- */

  captureNative();
  readStateFromUrl();
  setOpen(Boolean(state.q.trim()));
  syncFilterUI();
  // Open the desktop filters that hold an active YAGA option (like the theme does for its own).
  document.querySelectorAll('#FacetFiltersForm [data-yaga-custom-filter]').forEach((inputEl) => {
    if (!inputEl.checked) return;
    const details = inputEl.closest('details');
    if (details) details.open = true;
  });
  if (isActive()) render();
})();
